<template>
  <div class="search-bar">
    <!-- 定位部分 -->
    <div class="location-section">
      <div class="location" @click="toggleCitySelector">
        <i class="el-icon-location"></i>
        <span>{{ showCurrentCity }}{{ $t("station") }}</span>
        <i
          :class="
            isCitySelectorOpen ? 'el-icon-caret-top' : 'el-icon-caret-bottom'
          "
        ></i>
      </div>
      <span class="tips">{{ $t("departureLocation") }}</span>
    </div>
    <!-- 搜索部分 -->
    <div class="search-section">
      <el-input
        v-model="searchKeyword"
        :placeholder="$t('searchPlaceholder')"
        clearable
        class="search-input"
        @clear="handleClear"
      >
        <template #append>
          <el-button type="primary" @click="handleSearch">{{
            $t("search")
          }}</el-button>
        </template>
      </el-input>
    </div>
    <!-- 城市选择下拉面板 -->
    <div v-if="isCitySelectorOpen" class="city-selector-panel" @click.stop>
      <!-- 加载状态 -->
      <div v-if="isLoading" class="loading-container">
        <el-loading-spinner></el-loading-spinner>
        <span>{{ $t("loadingCityData") }}</span>
      </div>
      <!-- 错误状态 -->
      <div v-else-if="errorMessage" class="error-container">
        <i class="el-icon-error"></i>
        <span>{{ $t("loadCityFailed") }}</span>
        <el-button @click="fetchCities">{{ $t("retry") }}</el-button>
      </div>
      <!-- 城市数据 -->
      <div v-else>
        <!-- 最近切换城市 -->
        <div class="recent-cities">
          <div class="section-title">{{ $t("recentCities") }}</div>
          <div class="city-tags">
            <span
              v-for="city in recentCities"
              :key="city.id"
              class="city-tag"
              @click="selectCity(city)"
            >
              {{ $i18n.locale === "zh" ? city.zh_name : city.en_name }}
            </span>
            <span class="clear-btn" @click="clearRecentCities">{{
              $t("clear")
            }}</span>
          </div>
        </div>
        <!-- 城市搜索 -->
        <div class="city-search">
          <el-input
            v-model="citySearchKeyword"
            :placeholder="$t('searchCityPlaceholder')"
            class="search-input"
            @input="handleCitySearch"
          >
            <template #prepend>{{ $t("searchCity") }}</template>
          </el-input>
        </div>
        <!-- 城市字母索引 -->
        <div class="city-index">
          <span
            v-for="letter in cityIndexLetters"
            :key="letter"
            class="index-letter"
            @click="scrollToLetter(letter)"
          >
            {{ letter }}
          </span>
        </div>
        <!-- 城市列表 -->
        <div class="city-list" ref="cityList">
          <div
            v-for="(cities, letter) in groupedCities"
            :key="letter"
            class="city-group"
          >
            <div class="group-letter" :data-letter="letter">{{ letter }}</div>
            <div class="group-cities">
              <span
                v-for="city in cities"
                :key="city.id"
                class="city-item"
                @click="selectCity(city)"
              >
                {{ $i18n.locale === "zh" ? city.zh_name : city.en_name }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { pinyin } from "pinyin-pro";
import { getLoaction } from "..//utils/location.js";
export default {
  name: "SearchBar",
  props: {
    defaultSearch: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      currentCityInfo: null, // 始终存完整城市对象
      searchKeyword: "",
      isCitySelectorOpen: false,
      citySearchKeyword: "",
      // 初始值必须是「完整城市对象」，和接口返回格式一致
      recentCities: [
        {
          id: 1,
          zh_name: "上海",
          en_name: "Shanghai",
          initial: "S",
          initial_en: "S",
        },
        {
          id: 2,
          zh_name: "北京",
          en_name: "Beijing",
          initial: "B",
          initial_en: "B",
        },
        {
          id: 3,
          zh_name: "广州",
          en_name: "Guangzhou",
          initial: "G",
          initial_en: "G",
        },
      ],
      cityIndexLetters: [],
      allCities: {},
      isLoading: false,
      errorMessage: null,
    };
  },
  watch: {
    "$i18n.locale"() {
      this.fetchCities();
    },
    defaultSearch(newVal) {
      this.searchKeyword = newVal;
    },
  },
  computed: {
    // 当前显示城市：根据语言从存储的city对象里取对应名称
    showCurrentCity() {
      if (!this.currentCityInfo) return "";
      return this.$i18n.locale === "zh"
        ? this.currentCityInfo.zh_name
        : this.currentCityInfo.en_name;
    },
    // 热门城市展示：源数据存city对象，展示时取对应语言
    showRecentArr() {
      return this.recentCities.map((city) => {
        return this.$i18n.locale === "zh" ? city.zh_name : city.en_name;
      });
    },
    groupedCities() {
      if (!this.citySearchKeyword) return this.allCities;
      const filteredCities = {};
      const keyword = this.citySearchKeyword.trim().toUpperCase();
      const initField = this.$i18n.locale === "zh" ? "initial" : "initial_en";
      for (const letter in this.allCities) {
        const matchedCities = this.allCities[letter].filter((city) => {
          return (
            city.zh_name.toUpperCase().includes(keyword) ||
            city.en_name.toUpperCase().includes(keyword) ||
            city[initField].includes(keyword)
          );
        });
        if (matchedCities.length > 0) filteredCities[letter] = matchedCities;
      }
      return filteredCities;
    },
  },
  created() {
    this.searchKeyword = this.defaultSearch;
  },
  methods: {
    handleClear() {
      this.$emit("search-cleared");
    },
    toggleCitySelector() {
      // 只有当allCities为空、且未加载/无错误时，才重新请求
      if (
        !this.isLoading &&
        !this.errorMessage &&
        Object.keys(this.allCities).length === 0
      ) {
        this.fetchCities();
      }
      // 切换面板显示状态
      this.isCitySelectorOpen = !this.isCitySelectorOpen;
    },
    async fetchCities() {
      this.isLoading = true;
      this.errorMessage = null;
      try {
        // 接口不用传lang了（后端固定返回双语言字段）
        const res = await this.$get("/get_city_list");
        const rawList = res.data.data;
        console.log(rawList);
        const listData = rawList;
        // 根据当前语言选用首字母字段
        const initField = this.$i18n.locale === "zh" ? "initial" : "initial_en";
        const tempGroup = {};
        for (const letter in listData) {
          const arr = listData[letter];
          for (const item of arr) {
            const init = item[initField];
            if (!tempGroup[init]) tempGroup[init] = [];
            tempGroup[init].push(item);
          }
        }
        // 字母数组排序
        this.cityIndexLetters = Object.keys(tempGroup).sort((a, b) =>
          a.localeCompare(b),
        );
        this.allCities = tempGroup;
        this.getLocation();
      } catch (error) {
        console.error("获取城市数据失败", error);
        this.errorMessage = this.$t("loadCityFailed");
      } finally {
        this.isLoading = false;
      }
    },
    getCityZh(city) {
      for (const letter in this.allCities) {
        const cities = this.allCities[letter];
        const matchedCity = cities.find(
          (item) => item.en_name === city || item.zh_name === city,
        );
        if (matchedCity) {
          return matchedCity.zh_name;
        }
      }
      return city;
    },
    closeCitySelector(event) {
      const locationElement = this.$el.querySelector(".location");
      const panelElement = this.$el.querySelector(".city-selector-panel");
      if (
        this.isCitySelectorOpen &&
        locationElement &&
        !locationElement.contains(event.target) &&
        panelElement &&
        !panelElement.contains(event.target)
      ) {
        this.isCitySelectorOpen = false;
      }
    },
    // 关键：点击什么文字，直接原字符串传出，不再转中文
    selectCity(city) {
      // 防御性校验：如果传入的不是对象（比如误传字符串），先从allCities里找完整对象
      let targetCity = city;
      if (typeof city === "string") {
        // 遍历allCities，根据当前语言匹配zh_name/en_name，找到完整对象
        for (const letter in this.allCities) {
          const findCity = this.allCities[letter].find((item) => {
            return this.$i18n.locale === "zh"
              ? item.zh_name === city
              : item.en_name === city;
          });
          if (findCity) {
            targetCity = findCity;
            break;
          }
        }
      }

      // 兜底：如果还是找不到完整对象，直接返回，避免后续报错
      if (!targetCity || !targetCity.id) {
        console.warn("未找到完整城市信息", city);
        this.isCitySelectorOpen = false;
        return;
      }

      // 存储完整城市对象（核心：保证currentCityInfo始终有id/zh_name/en_name）
      this.currentCityInfo = targetCity;
      this.isCitySelectorOpen = false;

      // 去重存入最近城市列表（只存完整对象，通过id去重）
      if (!this.recentCities.some((item) => item.id === targetCity.id)) {
        this.recentCities.unshift(targetCity);
        // 最多保留5个最近城市
        if (this.recentCities.length > 5) this.recentCities.pop();
      }

      // 传给父组件对应语言的名称
      const sendName =
        this.$i18n.locale === "zh" ? targetCity.zh_name : targetCity.en_name;
      this.$emit("city-change", sendName);
    },

    // 清空最近切换城市列表
    clearRecentCities() {
      this.recentCities = [];
    },
    handleCitySearch() {
      this.citySearchKeyword = this.citySearchKeyword.toUpperCase();
    },
    scrollToLetter(letter) {
      const cityListContainer = this.$refs.cityList;
      if (!cityListContainer) return;
      const targetElement = this.$el.querySelector(
        `.group-letter[data-letter="${letter}"]`,
      );
      if (targetElement) {
        const offsetTop = targetElement.offsetTop;
        cityListContainer.scrollTo({
          top: offsetTop - 190,
          behavior: "smooth",
        });
      }
    },
    getFirstLetter(city) {
      const options = {
        toneType: "none",
        type: "array",
      };
      const pyArray = pinyin(city, options);
      return pyArray[0][0].toUpperCase();
    },
    getLocation() {
      if (navigator.geolocation) {
        getLoaction.getLocationAddress().then((locationData) => {
          let cityName = locationData.ad_info.city;
          // 核心：去掉城市名中的「市」字（比如「上海市」→「上海」）
          cityName = cityName.replace(/市/g, "");
          // 根据处理后的城市名去allCities匹配完整城市对象
          let findObj = null;
          console.log(this.allCities, "ppp");
          for (let key in this.allCities) {
            const item = this.allCities[key].find(
              (v) => v.zh_name === cityName,
            );
            if (item) {
              findObj = item;
              break;
            }
          }
          console.log(findObj);
          if (findObj) {
            this.currentCityInfo = findObj;
          } else {
            // 兜底：匹配不到时默认赋值上海
            const sh = this.recentCities.find(
              (item) => item.zh_name === "上海",
            );
            this.currentCityInfo = sh;
          }
        });
      } else {
        // 默认赋值上海完整对象
        const sh = this.recentCities.find((item) => item.zh_name === "上海");
        this.currentCityInfo = sh;
      }
    },
    handleSearch() {
      if (this.searchKeyword.trim()) {
        this.$emit("search", this.searchKeyword);
      }
    },
  },
  mounted() {
    this.fetchCities();
    // this.getLocation();
    document.addEventListener("click", this.closeCitySelector);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeCitySelector);
  },
};
</script>

<style scoped>
.search-bar {
  display: flex;
  align-items: center;
  background-color: #e6f7ff;
  padding: 10px 20px;
  margin-bottom: 20px;
  position: relative;
}

.location-section {
  display: flex;
  align-items: baseline;
  margin-right: 40px;
}

.location {
  display: flex;
  align-items: center;
  font-size: 18px;
  color: #409eff;
  cursor: pointer;
}

.tips {
  font-size: 12px;
  color: #f56c6c;
  font-weight: bold;
}

.location i {
  margin-right: 5px;
}

.local-tour-btn {
  background-color: transparent;
  border: 1px solid #409eff;
  color: #409eff;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.local-tour-btn:hover {
  background-color: #409eff;
  color: white;
}

.search-section {
  flex: 1;
}

.search-input {
  width: 400px;
}

.city-selector-panel {
  position: absolute;
  top: 100%;
  left: 0;
  width: 520px;
  background-color: white;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  z-index: 100;
  padding: 15px;
  margin-top: 5px;
}

.recent-cities {
  margin-bottom: 15px;
}

.section-title {
  font-weight: bold;
  margin-bottom: 10px;
}

.city-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.city-tag {
  padding: 5px 10px;
  background-color: #f5f7fa;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 14px;
}

.city-tag:hover {
  background-color: #e6f7ff;
}

.clear-btn {
  color: #409eff;
  cursor: pointer;
  margin-left: 10px;
  font-size: 14px;
}

.city-search {
  margin-bottom: 15px;
}

.city-index {
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  padding: 0 5px;
  border-bottom: 1px solid #ebeef5;
  padding-bottom: 10px;
  overflow-x: auto;
  padding-bottom: 5px;
}

.index-letter {
  cursor: pointer;
  color: #409eff;
  font-size: 14px;
  padding: 2px 5px;
  border-radius: 3px;
  white-space: nowrap;
  margin: 0 2px;
}

.index-letter:hover {
  background-color: #e6f7ff;
}

.city-list {
  max-height: 350px;
  overflow-y: auto;
  padding-right: 5px;
}

.city-group {
  margin-bottom: 15px;
}

.group-letter {
  font-weight: bold;
  color: #909399;
  margin-bottom: 10px;
  padding-left: 5px;
  border-left: 3px solid #409eff;
}

.group-cities {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.city-item {
  cursor: pointer;
  padding: 5px 5px;
  border-radius: 4px;
  transition: background-color 0.2s;
  min-width: 45px;
  text-align: center;
  font-size: 14px;
}

.city-item:hover {
  background-color: #e6f7ff;
}

.loading-container,
.error-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #606266;
}

.loading-container {
  min-height: 200px;
}

.error-container {
  flex-direction: column;
}

.error-container i {
  font-size: 24px;
  color: #f56c6c;
  margin-bottom: 10px;
}

.error-container button {
  margin-top: 10px;
}

@media screen and (max-width: 768px) {
  .search-bar {
    flex-direction: column;
    padding: 10px;
    align-items: stretch;
  }

  .location-section {
    margin-right: 0;
    margin-bottom: 10px;
  }

  .location {
    margin-right: 10px;
  }

  .search-section {
    width: 100%;
  }

  .search-input {
    width: 100% !important;
  }

  .city-selector-panel {
    width: calc(100vw - 70px);
  }

  .city-index {
    padding-bottom: 5px;
    margin-bottom: 10px;
  }

  .index-letter {
    font-size: 13px;
    padding: 1px 4px;
  }

  .city-item {
    min-width: 38px;
    font-size: 13px;
    padding: 4px 4px;
  }

  .city-list {
    max-height: 300px;
  }

  .local-tour-btn {
    padding: 4px 8px;
    font-size: 13px;
  }

  .recent-cities {
    margin-bottom: 10px;
  }

  .city-search {
    margin-bottom: 10px;
  }
}

@media screen and (max-width: 375px) {
  .location-section {
    flex-wrap: wrap;
  }

  .local-tour-btn {
    margin-top: 8px;
    width: 100%;
  }

  .city-selector-panel {
    max-height: 80vh;
    overflow-y: auto;
  }

  .city-list {
    max-height: 250px;
  }
}
</style>
